(() => {
  const SELECTORS = {
    root: "[data-ctr-science-ingredients]",
    tab: "[data-ctr-si-tab]",
    panel: "[data-ctr-si-panel]",
    track: "[data-ctr-si-track]",
    prev: "[data-ctr-si-prev]",
    next: "[data-ctr-si-next]",
    dots: "[data-ctr-si-dots]",
    dot: "[data-ctr-si-dot]",
  };

  const instances = new Map();

  class CtrScienceIngredients {
    constructor(section) {
      this.section = section;
      this.sectionId = section.dataset.sectionId || "";
      this.tabs = Array.from(section.querySelectorAll(SELECTORS.tab));
      this.panels = Array.from(section.querySelectorAll(SELECTORS.panel));
      this.track = section.querySelector(SELECTORS.track);
      this.prev = section.querySelector(SELECTORS.prev);
      this.next = section.querySelector(SELECTORS.next);
      this.dots = Array.from(section.querySelectorAll(SELECTORS.dot));

      this.handleTabClick = this.handleTabClick.bind(this);
      this.handleTabKeydown = this.handleTabKeydown.bind(this);
      this.handleDotClick = this.handleDotClick.bind(this);
      this.handlePrev = this.handlePrev.bind(this);
      this.handleNext = this.handleNext.bind(this);
      this.handleScroll = this.handleScroll.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.onSectionSelect = this.onSectionSelect.bind(this);
      this.onResize = this.onResize.bind(this);

      this.bind();
      this.syncArrows();
    }

    bind() {
      this.tabs.forEach((tab) => {
        tab.addEventListener("click", this.handleTabClick);
        tab.addEventListener("keydown", this.handleTabKeydown);
      });

      this.dots.forEach((dot) => {
        dot.addEventListener("click", this.handleDotClick);
      });

      if (this.prev) this.prev.addEventListener("click", this.handlePrev);
      if (this.next) this.next.addEventListener("click", this.handleNext);
      if (this.track) this.track.addEventListener("scroll", this.handleScroll, { passive: true });

      window.addEventListener("resize", this.onResize);
      document.addEventListener("shopify:section:select", this.onSectionSelect);
      document.addEventListener("shopify:section:deselect", this.onSectionSelect);
      document.addEventListener("shopify:block:select", this.onBlockSelect);
    }

    handleTabClick(event) {
      event.preventDefault();
      this.setActive(event.currentTarget.dataset.tabTarget, true);
    }

    handleDotClick(event) {
      event.preventDefault();
      this.setActive(event.currentTarget.dataset.tabTarget, true);
    }

    handleTabKeydown(event) {
      const currentIndex = this.tabs.indexOf(event.currentTarget);
      if (currentIndex < 0) return;

      const keyMap = {
        ArrowLeft: -1,
        ArrowUp: -1,
        ArrowRight: 1,
        ArrowDown: 1,
      };

      if (event.key === "Home") {
        event.preventDefault();
        this.tabs[0]?.focus();
        return;
      }

      if (event.key === "End") {
        event.preventDefault();
        this.tabs[this.tabs.length - 1]?.focus();
        return;
      }

      const direction = keyMap[event.key];
      if (!direction) return;

      event.preventDefault();
      const nextIndex = (currentIndex + direction + this.tabs.length) % this.tabs.length;
      this.tabs[nextIndex]?.focus();
    }

    handlePrev(event) {
      event.preventDefault();
      this.scrollBy(-1);
    }

    handleNext(event) {
      event.preventDefault();
      this.scrollBy(1);
    }

    handleScroll() {
      this.syncArrows();
    }

    scrollBy(direction) {
      if (!this.track) return;
      const amount = Math.max(this.track.clientWidth * 0.6, 120);
      this.track.scrollLeft += amount * direction;
    }

    setActive(targetId, scrollIntoView) {
      if (!targetId) return;

      this.tabs.forEach((tab) => {
        const isActive = tab.dataset.tabTarget === targetId;
        tab.classList.toggle("is-active", isActive);
        tab.setAttribute("aria-selected", isActive ? "true" : "false");
        tab.setAttribute("tabindex", isActive ? "0" : "-1");
      });

      this.panels.forEach((panel) => {
        const isActive = panel.dataset.tabPanel === targetId;
        panel.classList.toggle("is-active", isActive);
        if (isActive) {
          panel.removeAttribute("hidden");
        } else {
          panel.setAttribute("hidden", "");
        }
      });

      this.dots.forEach((dot) => {
        const isActive = dot.dataset.tabTarget === targetId;
        dot.classList.toggle("is-active", isActive);
        dot.setAttribute("aria-current", isActive ? "true" : "false");
      });

      if (scrollIntoView) {
        const activeTab = this.tabs.find((tab) => tab.dataset.tabTarget === targetId);
        activeTab?.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
      }
    }

    syncArrows() {
      if (!this.track) return;

      const maxScroll = this.track.scrollWidth - this.track.clientWidth;
      const atStart = this.track.scrollLeft <= 2;
      const atEnd = this.track.scrollLeft >= maxScroll - 2;
      const canScroll = maxScroll > 4;

      if (this.prev) this.prev.disabled = !canScroll || atStart;
      if (this.next) this.next.disabled = !canScroll || atEnd;
    }

    onSectionSelect(event) {
      const detail = event.detail || {};
      if (detail.sectionId !== this.sectionId) return;
      this.syncArrows();
    }

    onBlockSelect(event) {
      const detail = event.detail || {};
      if (detail.sectionId !== this.sectionId || !detail.blockId) return;

      const panel = this.section.querySelector(`[data-block-id="${detail.blockId}"]`);
      const targetId = panel?.dataset.tabPanel || detail.blockId;
      this.setActive(targetId, true);
    }

    onResize() {
      this.syncArrows();
    }

    destroy() {
      this.tabs.forEach((tab) => {
        tab.removeEventListener("click", this.handleTabClick);
        tab.removeEventListener("keydown", this.handleTabKeydown);
      });

      this.dots.forEach((dot) => {
        dot.removeEventListener("click", this.handleDotClick);
      });

      if (this.prev) this.prev.removeEventListener("click", this.handlePrev);
      if (this.next) this.next.removeEventListener("click", this.handleNext);
      if (this.track) this.track.removeEventListener("scroll", this.handleScroll);

      window.removeEventListener("resize", this.onResize);
      document.removeEventListener("shopify:section:select", this.onSectionSelect);
      document.removeEventListener("shopify:section:deselect", this.onSectionSelect);
      document.removeEventListener("shopify:block:select", this.onBlockSelect);
    }
  }

  const init = (root) => {
    const section = root?.querySelector?.(SELECTORS.root) || root;
    if (!section || !section.matches(SELECTORS.root)) return;
    if (instances.has(section)) return;
    instances.set(section, new CtrScienceIngredients(section));
  };

  const unload = (root) => {
    const section = root?.querySelector?.(SELECTORS.root) || root;
    if (!section || !section.matches(SELECTORS.root)) return;
    const instance = instances.get(section);
    if (!instance) return;
    instance.destroy();
    instances.delete(section);
  };

  const initAll = () => {
    document.querySelectorAll(SELECTORS.root).forEach((section) => init(section));
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
  } else {
    initAll();
  }

  document.addEventListener("shopify:section:load", (event) => init(event.target));
  document.addEventListener("shopify:section:unload", (event) => unload(event.target));
  document.addEventListener("shopify:section:reorder", initAll);
})();
